import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { a as getAuthClient } from './index2.js';

const checkSlot = (slot, allowedSlotElements, disallowedSlotElements) => {
    const allowedElements = (allowedSlotElements === null || allowedSlotElements === void 0 ? void 0 : allowedSlotElements[slot.name]) || [];
    const disallowedElements = (disallowedSlotElements === null || disallowedSlotElements === void 0 ? void 0 : disallowedSlotElements[slot.name]) || [];
    if (allowedElements.length === 0 && disallowedElements.length === 0) {
        return;
    }
    const nodes = slot.assignedNodes();
    nodes.forEach((node) => {
        const name = node.nodeName.toLowerCase();
        if ((allowedElements.length > 0 && !allowedElements.includes(name)) || disallowedElements.includes(name)) {
            node.parentElement.removeChild(node);
            // eslint-disable-next-line no-console
            console.error(`Element <${name}> is not allowed in slot "${slot.name}". It has been removed.`);
        }
    });
};
/**
 * Limits the kind of elements that can be inserted into a (named) slot of a web component.
 * Illegal elements are removed from the DOM.
 * This function must be called in componentDidLoad method of the web component.
 * It only works with shadow DOM.
 * @param host The host element of the component.
 * @param allowedSlotElements An object that defines the allowed element-names (in lowercase) for each slot.
 * */
const limitSlotElements = (host, options) => {
    const slots = host.shadowRoot.querySelectorAll("slot");
    slots.forEach((slot) => {
        checkSlot(slot, options.allowed, options.disallowed);
        slot.addEventListener("slotchange", () => {
            checkSlot(slot, options.allowed, options.disallowed);
        });
    });
};

const authImageCss = ":root,.uic-theme-responsive{--uxc-app-header-height:52px;--uxc-app-header-expandables-y-offset:56px;--uxc-app-header-expandables-min-width:300px;--uxc-app-header-z-index:400;--uxc-app-header-bg-color:var(--uic-color-blue5);--uxc-app-header-separator-color:var(--uic-color-white);--uxc-app-header-launcher-width:68px}@media only screen and (min-width: 1024px){.uic-theme-responsive{--uxc-app-header-height:68px;--uxc-app-header-expandables-y-offset:72px;--uxc-app-header-launcher-width:85px}}:root,.uic-theme-responsive{--uxc-app-toast-notifications-z-index:350}:host{display:block}";
const UxcAuthImageStyle0 = authImageCss;

const AuthImage = /*@__PURE__*/ proxyCustomElement(class AuthImage extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.errorEvent = createEvent(this, "uxError", 7);
        this.imageElement = null;
        this.authClient = null;
        this.handleSlot = () => {
            this.imageElement = this.host.querySelector("ux-image");
            this.requestImage();
        };
        this.handleToken = (token) => {
            this.token = token;
        };
        this.src = undefined;
        this.header = {};
        this.scope = null;
        this.token = null;
    }
    requestImage() {
        if (this.imageElement === null || !this.src || (Object.keys(this.header).length === 0 && this.token === null)) {
            return;
        }
        if (this.src === "" || this.authClient === null) {
            this.imageElement.error = true;
            return;
        }
        const headers = new Headers();
        if (this.token !== null) {
            headers.append("Authorization", `Bearer ${this.token}`);
        }
        for (const [key, value] of Object.entries(this.header)) {
            headers.append(key, value);
        }
        this.imageElement.spinner = true;
        this.imageElement.error = false;
        fetch(this.src, { headers })
            .then((response) => {
            if (response.ok) {
                return response.blob();
            }
            else {
                throw new Error("Network response was not ok.");
            }
        })
            .then((blob) => {
            const url = URL.createObjectURL(blob);
            this.imageElement.src = url;
            this.imageElement.spinner = false;
            const onReady = () => {
                URL.revokeObjectURL(url);
                this.imageElement.removeEventListener("uxReady", onReady);
            };
            this.imageElement.addEventListener("uxReady", onReady);
        })
            .catch((error) => {
            this.imageElement.error = true;
            this.errorEvent.emit({
                error,
                src: this.src
            });
        });
    }
    componentWillLoad() {
        this.authClient = getAuthClient(this.scope);
        this.token = this.authClient.getAccessToken();
    }
    componentDidLoad() {
        limitSlotElements(this.host, {
            allowed: {
                "": ["ux-image"]
            }
        });
        this.host.shadowRoot.addEventListener("slotchange", () => {
            this.handleSlot();
        });
        this.handleSlot();
    }
    connectedCallback() {
        this.authClient = getAuthClient(this.scope);
        this.authClient.subscribe(this.handleToken);
        this.token = this.authClient.getAccessToken();
    }
    disconnectedCallback() {
        var _a;
        (_a = this.authClient) === null || _a === void 0 ? void 0 : _a.unsubscribe(this.handleToken);
    }
    render() {
        return (h(Host, { key: '84da5a685dabfe032969399617da6f26338bcb34' }, h("slot", { key: '32bca33514f08c983f651954e28e8b86a3064788' })));
    }
    get host() { return this; }
    static get watchers() { return {
        "src": ["requestImage"],
        "header": ["requestImage"],
        "scope": ["requestImage"],
        "token": ["requestImage"]
    }; }
    static get style() { return UxcAuthImageStyle0; }
}, [1, "uxc-auth-image", {
        "src": [1],
        "header": [16],
        "scope": [1],
        "token": [32]
    }, undefined, {
        "src": ["requestImage"],
        "header": ["requestImage"],
        "scope": ["requestImage"],
        "token": ["requestImage"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["uxc-auth-image"];
    components.forEach(tagName => { switch (tagName) {
        case "uxc-auth-image":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, AuthImage);
            }
            break;
    } });
}

const UxcAuthImage = AuthImage;
const defineCustomElement = defineCustomElement$1;

export { UxcAuthImage, defineCustomElement };

//# sourceMappingURL=uxc-auth-image.js.map